import { gql } from '@apollo/client';
import { WAREHOUSE_EVENT_FIELDS } from './order_wh_events';
import { EXCEPTION_FIELDS } from './db_exceptions';
import { PRICING_OVERRIDE_FIELDS } from '../fragments/pricing_overrides';
import { SURVEY_QUESTION_FIELDS, SURVEY_RESPONSE_FIELDS } from '../fragments/surveys';
import { TAG_MAPPING_FIELDS } from '../fragments/tags';
import { DOCUMENT_FIELDS } from './documents';
import { NOTE_FIELDS } from './notes';
import { ITEM_FIELDS } from './items';
import { ORDER_REFERENCE_FIELDS } from '../fragments/order_references';
import { PROGRAM_FIELDS } from '../fragments/programs';

export const ORDER_FIELDS = gql`
    ${WAREHOUSE_EVENT_FIELDS}
    ${EXCEPTION_FIELDS}
    ${PRICING_OVERRIDE_FIELDS}
    ${TAG_MAPPING_FIELDS}
    ${DOCUMENT_FIELDS}
    ${NOTE_FIELDS}
    ${ITEM_FIELDS}
    ${ORDER_REFERENCE_FIELDS}
    ${PROGRAM_FIELDS}

    fragment OrderFields on orders {
        admin_carrier_rate_override
        admin_contractor_rate_override
        admin_middle_mile_carrier_rate_override
        admin_middle_mile_shipper_rate_override
        admin_shipper_rate_override
        admin_warehouse_shipper_rate_override
        admin_warehouse_carrier_rate_override
        airline
        apartment_number
        approved
        assembly_required
        assigned_admin_id
        auto_assign_failed
        billing_completion_time
        billing_completion_date_formatted @client
        bol_file
        bol_file_name
        pickup_notification
        waybill
        broker_billed_rate
        broker_paid_rate
        business
        bungii_id
        came_from_slumberland_api
        carrier
        carrier_assignment_source
        carrier_change
        carrier_contact
        carrier_costs
        carrier_email
        carrier_id
        carrier_info
        carrier_net_terms
        carrier_invoice_id
        customer_rate
        carrier_phone
        carrier_price
        carrier_rate
        carrier_rate_override_id
        contractor_id
        contractor_invoice_id
        contractor_rate
        contractor_rate_override_id
        custom_fields
        geocoding
        is_middle_mile
        is_same_day
        charge_to
        chosen_store_name
        claimed_date
        claimed_date_formatted
        completion_date_formatted @client
        completion_time
        confirmation_source
        confirmed_date
        created_at
        cubic_feet
        curri_id
        custom_shape
        customco_id
        customer_budget
        customer_instructions
        customer_signature
        customer_signature_meta
        default_pickup_details_used
        delayed_by_consignee_ts
        del_elev
        del_end
        del_hour
        del_stairs
        del_window
        del_window_delay
        del_window_end
        del_window_start
        deliver_by
        delivery_date
        target_delivery_date
        target_delivery_date_formatted @client
        delivery_date_formatted @client
        delivery_photo
        delivery_photo_filename
        delivery_requests
        delivery_stair_quantity
        delivery_time_confirmed
        details
        dispatch_integration_meta
        dispatch_integration_type
        distance
        distance_failed
        dropoff_address_confirmed
        dropoff_address
        dropoff_address_confirmed
        dropoff_address_geocode_failed
        dropoff_address_is_custom
        dropoff_check
        dropoff_city
        dropoff_contact_same_as_pickup
        dropoff_contact_same_as_sender
        dropoff_date
        dropoff_elevator
        dropoff_email
        dropoff_error
        dropoff_floor
        dropoff_full_address
        dropoff_lat
        dropoff_lng
        dropoff_location
        dropoff_location_info
        dropoff_location_type
        dropoff_name
        dropoff_phone
        dropoff_point
        dropoff_secondary_contact
        dropoff_secondary_phone
        dropoff_state
        dropoff_street
        dropoff_street_address
        dropoff_unit
        dropoff_zip
        duration
        duration_seconds
        email
        equipment_overrides
        estimated_del_window
        estimated_del_window_string
        estimated_delivery_range_start
        estimated_delivery_range_end
        estimated_delivery_range_start_formatted @client
        estimated_delivery_range_end_formatted @client
        event_state
        external_integration_attr
        external_integration_id @client
        finalize_display
        first_available_date
        first_available_date_formatted @client
        footboard
        footboard_count
        form_type
        formatted_timestamp
        forward_stocked_status
        frayt_id
        freight_type
        full_truck
        furniture
        furniture_count
        gate_code
        has_middle_mile_origin
        has_secondary_contact
        haul_fridge
        haulaway
        haulaway_check
        haulaway_elevator
        haulaway_items
        haulaway_items_changed
        haulaway_options_changed
        haulaway_stair_quantity
        haulaway_stairs
        headboard
        headboard_count
        incomplete_reason
        invoice_number
        integration_reservation_id
        item_count
        item_description
        item_display
        item_error
        item_labels
        item_type
        item_type_details
        item_type_display
        items_changed
        items_total_cubes
        items_total_quantity
        items_total_weight
        job_id
        known_availability
        distance_integration_type
        last_modified_at
        last_modified_by
        last_modified_formatted
        last_modified_source
        last_update_source
        legacy_carrier_id
        legacy_order_id
        legacy_shipper_id
        loading
        loading_completed
        ltl_dropoff_arrival
        ltl_pickup_completed
        manual_scheduled_delivery
        manufacturer
        margin
        mark_for_reschedule
        metro_id
        middle_mile_carrier_id
        middle_mile_carrier_rate
        middle_mile_carrier_rate_override_id
        middle_mile_integration_meta
        middle_mile_integration_status
        middle_mile_integration_type
        middle_mile_is_direct
        middle_mile_origin_address
        middle_mile_origin_city
        middle_mile_origin_email
        middle_mile_origin_lat
        middle_mile_origin_lng
        middle_mile_origin_location
        middle_mile_origin_location_info
        middle_mile_origin_location_type
        middle_mile_origin_name
        middle_mile_origin_phone
        middle_mile_origin_state
        middle_mile_origin_unit
        middle_mile_origin_zip
        middle_mile_pod
        middle_mile_pricing_options
        middle_mile_shipper_rate
        middle_mile_shipper_rate_override_id
        miles
        pc_miler_version
        name
        new_pod
        no_contact
        oms
        onward_fees
        onward_message
        onward_rate_override_id
        order_change
        order_date
        order_id
        order_number
        order_revenue
        order_revenue_adjustments
        order_revenue_override
        order_status
        order_type
        order_updated_hash
        original_carrier_rate
        original_del_window_end
        original_del_window_start
        original_order_id
        original_shipper_rate
        page
        pallet_count
        pallet_debris_removal_notes
        pallet_display
        pallet_expedite
        pallet_flatbed_truck
        pallet_forklift_at_dropoff
        pallet_forklift_at_pickup
        pallet_overweight
        pallet_unloading_required
        pallet_warehouse_close_time
        palletize_returns
        palletized_dimensions
        pay_out_status
        pay_status
        pay_type
        payment_id
        payment_status
        payment_type
        phone
        pickup_address
        pickup_address_geocode_failed
        pickup_address_is_custom
        pickup_city
        pickup_date
        pickup_date_formatted @client
        pickup_elevator
        pickup_email
        pickup_end
        pickup_error
        pickup_full_address
        pickup_hour
        pickup_lat
        pickup_lng
        pickup_location
        pickup_location_info
        pickup_location_type
        pickup_name
        pickup_phone
        pickup_point
        pickup_planning
        pickup_scheduling_required
        pickup_secondary_contact
        pickup_stair_quantity
        pickup_stairs
        pickup_state
        pickup_street
        pickup_street_address
        pickup_unit
        pickup_window_end
        pickup_window_start
        pickup_zip
        piece_count_verified
        pin_labels
        planning
        po_duplicate_override
        po_number
        pod
        pod_file_name
        posted_date
        posted_date_formatted
        preferred_delivery_date
        preferred_delivery_date_formatted @client
        preferred_driver_id
        prescheduled
        previous_order_status
        price_breakdown
        priced_as_middle_mile
        pricing_options
        proof_of_pickup
        invoice_class
        invoice_notes
        qbo_tag
        quote_sent
        read_only
        est_ship_date
        est_received_date
        received_date
        mm_scheduled_ship_date
        mm_ship_date
        mm_received_date
        ship_date
        scheduled_received_date
        arrived_date
        ready_to_invoice
        invoicing_status
        recommended_equipment
        reference_id
        reschedule_reason_code
        reschedule_reason_notes
        residence_type
        resource_id
        return
        return_order_id
        returns_total_cubes
        returns_total_quantity
        returns_total_weight
        scheduled_delivery
        secondary_dropoff_contact_email
        secondary_dropoff_contact_name
        secondary_dropoff_contact_phone
        secondary_email
        secondary_name
        secondary_phone
        secondary_pickup_contact_email
        secondary_pickup_contact_name
        secondary_pickup_contact_phone
        selected_type
        send_sms
        sending_to
        service_catalog_filter
        service_id
        service_level_id
        service_level_instructions
        service_time
        shipper_id
        shipper_invoice_id
        shipper_rate
        shipper_rate_override_id
        show_carrier_new_shipment_info_modal
        show_modal
        show_revenue_on
        show_shipper_new_shipment_info_modal
        source_form
        special_instructions
        special_pallet_info
        stairs_flights
        submit_error
        submit_failure
        template_name
        test_acc
        tforce_id
        timestamp_checks
        total_haulaway_items
        twilio_sid
        alternative_delivery_dates
        alternative_delivery_dates_formatted @client
        validation
        warehouse_control_entity
        warehouse_file
        warehouse_delivery_status
        warehouse_delivery_notes
        warehouse_estimated_ship_date
        warehouse_estimated_delivery_date
        warehouse_trailer_id
        warehouse_manifest_number
        warehouse_status
        warehouse_shipper_rate
        warehouse_shipper_rate_override_id
        warehouse_carrier_rate
        warehouse_carrier_id
        warehouse_carrier_rate_override_id
        exceptions {
            created_at
            exception_type
            event_id
            exception {
                ...ExceptionFields
            }
            item {
                ...ItemFields
            }
        }
        order_exception @client {
            ...ExceptionFields
        }
        wh_events(order_by: [{ created_at: asc_nulls_first }, { transition: { current: asc } }, { event_id: asc }]) {
            ...WarehouseEvents

            transition {
                next
                current
            }
        }
        shipper_rate_override {
            ...PricingOverrideFields

            assign {
                client_id
                business_name
                profile_img
                profile_img_color
            }

            partner_client {
                client_id
                business_name
                profile_img
                profile_img_color
            }
        }
        onward_rate_override {
            ...PricingOverrideFields

            assign {
                client_id
                business_name
                profile_img
                profile_img_color
            }

            partner_client {
                client_id
                business_name
                profile_img
                profile_img_color
            }
        }
        documents {
            mapping_id
            document_id
            order_id
            document {
                ...DocumentFields
            }
        }
        tags {
            ...TagMappingFields
        }
        notes {
            ...NoteFields
        }

        email_subscriptions(where: { enabled: { _eq: true } }) {
            subscription_id
            event
            email
            enabled
        }

        references {
            ...OrderReferenceFields
        }

        program {
            ...ProgramFields
        }
    }
`;

export const ORDER_ROUTE_FIELDS = gql`
    fragment OrderRouteFields on orders {
        routes {
            mapping_id
            order_id
            route_id
            type
        }
        order_shipper {
            client_id
            business_name
        }
    }
`;

/**
 * Exactly the columns the `carrier/unassign` endpoint writes — the `clearUpdate`
 * in onward-functions `src/lib/integrations/unassignCarrier.f.js` — plus the
 * route mappings its line-haul detach deletes.
 *
 * Narrow on purpose. Re-reading `...OrderFields` would re-execute a dozen nested
 * joins to refresh ten scalars, and replacing the edit modal's whole order would
 * discard unsaved edits sitting in its other tabs. `order_id` is required: it is
 * the `orders` keyField, and it is what lets this read update the normalized
 * cache entry every list and detail query behind the modal already watches.
 *
 * Adding a column to that endpoint's `clearUpdate` means adding it here, or it
 * goes stale until the next full order read. `orders.carrierAssignment.test.js`
 * guards the pairing.
 */
export const ORDER_CARRIER_ASSIGNMENT_FIELDS = gql`
    fragment OrderCarrierAssignmentFields on orders {
        order_id
        carrier_id
        carrier_assignment_source
        dispatch_integration_type
        dispatch_integration_meta
        external_integration_attr
        frayt_id
        middle_mile_carrier_id
        middle_mile_integration_type
        middle_mile_integration_status
        middle_mile_integration_meta
        routes {
            mapping_id
            order_id
            route_id
            type
        }
        order_carrier {
            client_id
            business_name
        }
        order_middle_mile_carrier {
            client_id
            business_name
        }
    }
`;

export const QUERY_CARRIER_ASSIGNMENT_BY_ID = gql`
    ${ORDER_CARRIER_ASSIGNMENT_FIELDS}

    query OrderCarrierAssignment($order_id: uuid!) {
        order: orders_by_pk(order_id: $order_id) {
            ...OrderCarrierAssignmentFields
        }
    }
`;

/**
 * The order columns the dispatch board's stop mutations write via
 * `$order_updates`, and nothing else. Scalars only — no joins, no computed
 * fields — so `update_orders_many` can return it per update block for the
 * price of the UPDATE it already ran.
 *
 * This is the union of every `_set` any UPDATE_ROUTE_STOPS /
 * UNASSIGN_ORDERS_UPDATE / ASSIGN_ORDERS_UPSERT caller builds. It exists
 * because returning `...OrderFields` (~480 lines, a dozen nested joins)
 * re-executed that whole subtree once per block and blew Hasura's 60s
 * operation limit on large routes (ENG-9527). Returning the narrow set keeps
 * the server authoritative over the cache without the subtree cost.
 *
 * Adding a field to a caller's `_set` means adding it here, or that column
 * goes stale in the cache until the next full order read.
 */
export const ORDER_DISPATCH_SCALARS = gql`
    fragment OrderDispatchScalars on orders {
        order_id
        order_status
        carrier_id
        contractor_id
        pickup_date
        delivery_date
        del_window_start
        del_window_end
        original_del_window_start
        original_del_window_end
        delivery_time_confirmed
        confirmation_source
        resource_id
    }
`;

export const FULL_QUERY = gql`
    ${ORDER_FIELDS}
    query GetOrders {
        orders(limit: 100) {
            ...OrderFields
        }
    }
`;

export const QUERY_BY_ID = gql`
    ${ORDER_FIELDS}
    ${SURVEY_QUESTION_FIELDS}
    ${SURVEY_RESPONSE_FIELDS}

    query orders($order_id: uuid) {
        orders(limit: 100, where: { order_id: { _eq: $order_id } }) {
            ...OrderFields
            survey_responses {
                ...SurveyResponseFields
                survey_question {
                    ...SurveyQuestionFields
                }
            }
        }
    }
`;

export const QUERY_BY_STATE = gql`
    query orders($created_at: timestamptz) {
        orders(distinct_on: dropoff_state, where: { test_acc: { _eq: false }, created_at: { _gte: $created_at } }) {
            dropoff_state
            order_number
            order_id
            created_at
        }
    }
`;
